import { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useStore, DEMO_MODE } from '../store/StoreContext.jsx';
import { getRecordedFacts, getComparison, readPreparation } from '../store/assessmentFacts.js';
import { buildTakeaway, takeawayLimitations } from '../store/takeaway.js';
import useAssessmentSync from '../store/useAssessmentSync.js';
import { describeSync, ASSESSMENT_LIMIT } from '../store/assessmentSync.js';
import { isEditableTarget } from '../store/meetingSections.js';
import {
  DEFAULT_INK,
  BOARD_STEP,
  FIRST_BOARD,
  boardKeys,
  nextBoardKey,
  newIllustration,
  putIllustration,
  removeIllustration,
  listIllustrations,
  findForMoment,
  sameBasis,
  commitStroke,
  undoStroke,
  clearStrokes,
  hasInk,
  strokesOf,
  illustrationsBytes,
} from '../store/illustrations.js';
import {
  analysisAvailability,
  isReady,
  batchKey,
  getCompanyExposure,
  getEquityStyle,
  getHouseholdContext,
  getAnalysisScope,
} from '../store/portfolioAnalysis.js';
import { buildAssessmentView } from '../store/assessmentView.js';
import { buildPages, slideBasis } from '../store/assessmentPages.js';
import { captureSlide } from '../store/slideCapture.js';
import usePairing, { pairCodeFromUrl, newPairCode } from '../store/usePairing.js';
import SavedSlide from '../components/conversation/SavedSlide.jsx';
import StatementsDialog from '../components/conversation/StatementsDialog.jsx';
import Slide from '../components/conversation/Slide.jsx';
import PageBody from '../components/conversation/PageBody.jsx';
import { MeetingProvider, useProfileSave } from '../components/conversation/Peek.jsx';
import MeetingMenu, { DrawWidget, AdjustWidget } from '../components/conversation/MeetingMenu.jsx';
import { useCompactLayout, StepList, BoardChips, CompactBar, SplitBar, TakeawayIcon } from '../components/conversation/MeetingNav.jsx';
import { useInk } from '../components/conversation/InkLayer.jsx';
import WhiteboardDialog from '../components/conversation/WhiteboardDialog.jsx';
import IllustrationsDrawer from '../components/conversation/IllustrationsDrawer.jsx';
import PairDialog from '../components/conversation/PairDialog.jsx';
import SplitDivider, { useSplit } from '../components/conversation/SplitDivider.jsx';
import { useAnchoredInk, ANCHORED_INK_STEP } from '../components/conversation/AnchoredInk.jsx';
import TakeawayView from '../components/conversation/TakeawayView.jsx';
import DemoExampleBar from '../components/conversation/DemoExampleBar.jsx';
import useDemoExamples from '../store/useDemoExamples.js';
import '../conversation.css';

// The redesigned free assessment: one saved record, two modes and a dialog.
//
// Meet is what the prospect sees, in two parts (Amanda Cook, 2026-10-02, and
// Jimmy, 2026-10-03). The first is the meeting as advisors run it: the goal
// sheet, which the advisor prepares beforehand the way they always have, and
// then Illustrations, blank whiteboards for the tax triangle and whatever else
// is drawn by hand. The rest is follow-up reading, in the takeaway for the
// client to go through afterwards and one click away if the conversation turns
// to it: where the money sits, cash flow, what the statements show and what it
// costs (those two only once statements are accepted), what protects it, and
// what planning does next.
//
// Every page is laid out at one fixed size and scaled to the screen, and the
// takeaway prints the same pages from the same page models, so anything drawn
// on a page prints exactly where it was drawn, and the whiteboards print at
// the back. Every figure is the questionnaire's own, set as finished type and
// opened by a click when the client corrects it; every finding is a rule's,
// and the advisor can leave any one out beforehand.
//
// On a phone, a tablet or a small window the column of pages becomes one slim
// bar, across the top or down the side, and the page always fits the screen
// (components/conversation/MeetingNav.jsx).
//
// The meeting can be on two devices at once. The screen the prospect watches
// shows a code, the advisor scans it with an iPad, and the iPad joins as a
// companion: the same pages, turned together, with whatever is drawn on the
// iPad appearing on the screen as it is drawn (store/usePairing.js). Only the
// screen saves.
//
// Takeaway is the landscape deliverable. Statements is the one thing a
// planning admin does beforehand: upload, review, accept. There is no separate
// preparation record; the questionnaire is the record, which is what keeps
// this and the eMoney push telling the same story.
//
// What the assessment itself owns lives under `assessment.meta` (the accepted
// analysis, the meeting's choices, the takeaway's choices) and the drawings
// under `assessment.sketches.illustrations`, both on parts of the sync that
// already exist, which is why this screen asks Salesforce for nothing new.

// The AI planning brief lives on its own screen (/assessment-brief), in the
// environments that have the Claude route: pq-dev, staging and local runs.
// The same compile-time constant App.jsx reads (vite.config.js).
// eslint-disable-next-line no-undef
const BRIEF_ENABLED = __BRIEF_BUILD__;

// Where the brief exists, it replaces the follow-up slides (Jimmy, 2026-10-14
// and 15): the meeting screen is the brief down the left, the goal sheet as
// its first page, and the whiteboard on the right, with nothing that looks
// like a slide; the takeaway is the same pages with the whiteboards at the
// back. The menu is in the header and the Meet / Takeaway switch opens the
// takeaway. `?slides=1` still reaches the October slides, the way `?classic=1`
// reaches the report before them, and carries the browser suite that proves
// their figures. The public demo has no brief, so it keeps the slides.
const SLIDE_FLOW = !BRIEF_ENABLED
  || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('slides') === '1');
const BRIEF_FLOW = !SLIDE_FLOW;

// The brief's parts for the meeting load with the brief, never with the demo.
const BriefBeside = BRIEF_ENABLED
  ? lazy(() => import('../brief/BriefInMeeting.jsx').then(m => ({ default: m.BriefBeside })))
  : null;
const BriefTakeaway = BRIEF_ENABLED
  ? lazy(() => import('../brief/BriefInMeeting.jsx').then(m => ({ default: m.BriefTakeaway })))
  : null;

const ALL_STEPS = [
  { key: 'goals', n: '01', label: 'Goals', group: 'meeting' },
  { key: BOARD_STEP, n: '02', label: 'Illustrations', group: 'meeting' },
  { key: 'tax', n: '03', label: 'Tax', group: 'reading' },
  { key: 'cash', n: '04', label: 'Cash flow', group: 'reading' },
  { key: 'investments', n: '05', label: 'Investments', group: 'reading' },
  { key: 'costs', n: '06', label: 'Costs', group: 'reading' },
  { key: 'protection', n: '07', label: 'Protection', group: 'reading' },
  { key: 'next', n: '08', label: 'Next', group: 'reading' },
];
// In the brief flow the only page turned is the whiteboard; the goal sheet is
// the brief's first page, beside it.
const STEPS = SLIDE_FLOW ? ALL_STEPS : ALL_STEPS.filter(s => s.key === BOARD_STEP);
const BOARD_INDEX = STEPS.findIndex(s => s.key === BOARD_STEP);

// The rail is the agenda: the same two parts for every household, told apart
// by a line. The names are for screen readers.
const GROUPS = [
  { key: 'meeting', label: 'In our meeting' },
  { key: 'reading', label: 'Follow-up reading' },
].filter(g => SLIDE_FLOW || g.key === 'meeting');

const MODES = [
  { key: 'meet', label: 'Meet' },
  { key: 'takeaway', label: 'Takeaway' },
];

const fmtDate = d => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const STATEMENT_WORDS = {
  absent: 'Statements',
  pending: 'Statements · reading',
  failed: 'Statements · failed',
  unreviewed: 'Statements · to review',
  ready: 'Statements · accepted',
};

// Opens it in place, keeping the household already loaded: the app turns a
// history change into a screen change.
function openBrief() {
  window.history.pushState({}, '', `/assessment-brief${window.location.search}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function leadIdFromUrl() {
  if (typeof window === 'undefined') return '';
  const p = new URLSearchParams(window.location.search);
  return p.get('leadId') || p.get('lead') || '';
}

export default function ConversationScreen({ onBack }) {
  const { data, update, replace, readOnly } = useStore();

  // Opened from the code on the meeting screen, this page is that screen's
  // companion: it shows what the screen holds and saves nothing of its own.
  const pairParam = useMemo(pairCodeFromUrl, []);
  const companion = Boolean(pairParam);

  const [mode, setMode] = useState('meet');
  // In the brief flow the takeaway can be printed once the brief is reviewed.
  const [briefReady, setBriefReady] = useState(false);
  // The takeaway's print: recorded for compliance, then printed (brief/assessmentRecord.js).
  const briefPrint = useRef(null);
  const [briefPrinting, setBriefPrinting] = useState(false);
  // On a phone or a tablet, which of the brief and the whiteboard is shown.
  const [splitView, setSplitView] = useState('board');
  // On a laptop, how the screen is divided between them.
  const split = useSplit();
  const splitRef = useRef(null);
  const [step, setStep] = useState(0);
  const [boardKey, setBoardKey] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [color, setColor] = useState(DEFAULT_INK);
  const [clearOpen, setClearOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [statementsOpen, setStatementsOpen] = useState(false);
  const [legacyBoard, setLegacyBoard] = useState(null);
  const [pairOpen, setPairOpen] = useState(false);
  const [hostCode, setHostCode] = useState('');
  const [theirLine, setTheirLine] = useState(null);
  const slideRef = useRef(null);
  const stageRef = useRef(null);
  const [savedPreview, setSavedPreview] = useState(null);
  const previewRef = useRef(null);
  const [adjusting, setAdjusting] = useState(false);

  const leadId = useMemo(() => data?._leadId || leadIdFromUrl(), [data?._leadId]);
  const { sync, markChanged } = useAssessmentSync({
    leadId,
    // The companion is given the drawings by the screen, not by Salesforce.
    enabled: Boolean(leadId) && !DEMO_MODE && !companion,
    readOnly,
    assessment: data?.assessment,
    update,
  });

  // The demo's example households. `DEMO_MODE` is set at build time, so on any
  // other deployment this hook does nothing and the bar is never rendered. A
  // companion shows the household the screen is showing, so it has no bar.
  const demo = useDemoExamples({ enabled: DEMO_MODE && !companion, data, replace });

  const facts = useMemo(() => getRecordedFacts(data), [data]);
  const comparison = useMemo(() => getComparison(facts), [facts]);
  const prepare = useMemo(() => readPreparation(data), [data]);
  const sketches = data?.assessment?.sketches;
  const illustrations = useMemo(() => listIllustrations(sketches), [sketches]);
  // What was drawn over the brief and the goal sheet prints on them, not as an
  // illustration at the back, so it is kept apart from the whiteboards.
  const drawings = useMemo(() => illustrations.filter(i => i.step !== ANCHORED_INK_STEP), [illustrations]);
  const briefInk = useMemo(() => findForMoment(sketches, ANCHORED_INK_STEP, null), [sketches]);

  const stepKey = STEPS[step].key;
  const onBoard = stepKey === BOARD_STEP;
  const compact = useCompactLayout();

  // The whiteboards. One is always on the sheet: the one chosen, else the
  // first, else a blank first one. A board exists once it has ink, so the
  // blank sheet being looked at is listed with the ones that do.
  const activeBoard = boardKey || boardKeys(sketches)[0] || FIRST_BOARD;
  const shownBoards = useMemo(() => boardKeys(sketches, activeBoard), [sketches, activeBoard]);
  const momentBoard = onBoard ? activeBoard : null;

  // The optional analysis. Nothing here is a meeting dependency: when it is not
  // ready the two statement slides are greyed rather than shown empty, and
  // every other slide is unaffected.
  const bundle = data?.assessment?.holdings || null;
  const uploadStatus = data?.assessment?.analysis || null;
  const currentBatch = batchKey(bundle);
  const accepted = Boolean(currentBatch && prepare.analysisAccepted === currentBatch);

  const analysis = useMemo(() => {
    const availability = analysisAvailability({ bundle, status: uploadStatus, accepted });
    if (!isReady(availability)) return { availability, simulated: false };
    const style = getEquityStyle(bundle);
    return {
      availability,
      exposure: getCompanyExposure(bundle),
      style,
      context: getHouseholdContext(facts, bundle, style),
      scope: getAnalysisScope(bundle),
      simulated: Boolean(bundle?.simulated),
    };
  }, [bundle, uploadStatus, accepted, facts]);

  // The reading of the household behind every slide, from the older report's
  // selectors, with blanks withheld and statements counted only once accepted.
  // The statements count only while their analysis is ready: accepted, and not
  // replaced by a read that is still running or failed.
  const analysisReady = isReady(analysis.availability);
  const view = useMemo(
    () => buildAssessmentView(data, { facts, comparison, accepted: analysisReady }),
    [data, facts, comparison, analysisReady],
  );
  const pages = useMemo(() => buildPages({ facts, view, analysis }), [facts, view, analysis]);
  const asOf = useMemo(() => fmtDate(new Date()), []);

  // The packet is the same slides, with the ink drawn on them.
  const takeaway = useMemo(
    () => buildTakeaway({ facts, analysis, illustrations: drawings, prepare, view, asOf, demo: DEMO_MODE }),
    [facts, analysis, drawings, prepare, view, asOf],
  );
  const limitations = useMemo(
    () => takeawayLimitations(facts, comparison, analysis),
    [facts, comparison, analysis],
  );

  // A slide with nothing to show for this household (no accepted statements,
  // say) is greyed so an advisor can see what it is waiting for, and Next
  // steps over it rather than into a dead end.
  const stepList = STEPS.map(s => {
    const available = Boolean(pages[s.key]);
    return {
      ...s,
      available,
      reason: available ? undefined : analysis.availability?.reason || 'No accepted statement analysis',
    };
  });
  const enabled = stepList.map((s, i) => (s.available ? i : -1)).filter(i => i >= 0);
  const nextStep = enabled.find(i => i > step);
  const prevStep = [...enabled].reverse().find(i => i < step);

  // The meeting's own choices, saved with the assessment: which years the
  // sheet reads, which findings are left out, the advisory fee.
  const setMeeting = useCallback(
    (key, value) => {
      if (readOnly) return;
      update(`assessment.meta.meeting.${key}`, value);
      markChanged('meta');
    },
    [readOnly, update, markChanged],
  );
  const hidden = view.meeting.hidden;
  const hideCallout = useCallback(key => setMeeting('hidden', [...new Set([...hidden, key])]), [setMeeting, hidden]);
  const showCallout = useCallback(key => setMeeting('hidden', [...hidden].filter(k => k !== key)), [setMeeting, hidden]);

  const setPrepare = useCallback(
    (key, value) => {
      if (readOnly) return;
      update(`assessment.meta.prepare.${key}`, value);
      markChanged('meta');
    },
    [readOnly, update, markChanged],
  );

  // The drawings, written to this device's copy and, on the screen, saved. A
  // stroke from the iPad and one from the screen can arrive in the same
  // moment, so the latest set is kept by hand rather than read back from a
  // render that has not happened yet. On a companion `markChanged` does
  // nothing: the screen is the one that saves.
  const sketchesRef = useRef(sketches);
  sketchesRef.current = sketches;
  const putSketch = useCallback(
    next => {
      sketchesRef.current = putIllustration(sketchesRef.current, next);
      update('assessment.sketches', sketchesRef.current);
      markChanged('sketches');
    },
    [update, markChanged],
  );

  // ---- The screen and the iPad -------------------------------------------

  const pairStore = `pq-pair:${DEMO_MODE ? 'demo' : leadId || 'none'}`;
  useEffect(() => {
    if (companion) return;
    try {
      const kept = sessionStorage.getItem(pairStore);
      if (kept) setHostCode(kept);
    } catch { /* a blocked store only means the code is not kept across a reload */ }
  }, [companion, pairStore]);

  const startPairing = () => {
    if (!hostCode) {
      const code = newPairCode();
      try { sessionStorage.setItem(pairStore, code); } catch { /* as above */ }
      setHostCode(code);
    }
    setPairOpen(true);
  };

  const navRef = useRef({ step: stepKey, board: momentBoard });
  navRef.current = { step: stepKey, board: momentBoard };

  const applyNav = nav => {
    if (!nav) return;
    if (nav.mode === 'meet' || nav.mode === 'takeaway') setMode(nav.mode);
    const i = STEPS.findIndex(s => s.key === nav.step);
    if (i >= 0) setStep(i);
    setBoardKey(nav.board || null);
  };

  // The screen's state, arriving on the iPad. While a stroke made here is
  // still on its way to the screen, the screen's drawings are a moment
  // behind, so this device's own are kept until it has caught up.
  const applyState = (next, caughtUp) => {
    const incoming = caughtUp
      ? next
      : { ...next, assessment: { ...(next?.assessment || {}), sketches: sketchesRef.current } };
    sketchesRef.current = incoming?.assessment?.sketches;
    replace(incoming);
  };

  // A stroke finished on the iPad, or taken back there, applied to the
  // screen's own copy of that page's drawing and saved from here.
  const applyInk = msg => {
    const found = findForMoment(sketchesRef.current, msg.step, msg.board, msg.basis);
    const base = found || {
      ...newIllustration({ kind: 'moment', step: msg.step, board: msg.board || null, basis: msg.basis }),
      ...(msg.id ? { id: msg.id } : {}),
    };
    let next = base;
    if (msg.op === 'stroke' && msg.stroke) {
      // A stroke sent again because this screen was reloading when it first
      // arrived may already be here.
      const last = base.strokes[base.strokes.length - 1];
      if (last && JSON.stringify(last) === JSON.stringify(msg.stroke)) return;
      next = commitStroke(base, msg.stroke);
    } else if (msg.op === 'undo') next = undoStroke(base);
    else if (msg.op === 'clear') next = clearStrokes(base);
    // The page as it stood when it was drawn on, kept for printing, is taken
    // from this screen when it is showing the same page.
    const here = navRef.current;
    if (!next.capture && hasInk(next) && msg.step === here.step && (msg.board || null) === (here.board || null) && slideRef.current) {
      next = { ...next, capture: captureSlide(slideRef.current) };
    }
    putSketch(next);
  };

  // The line the other device is drawing. When it stops, the line stays until
  // the finished stroke arrives in its place, so it never blinks out.
  const lineTimer = useRef(null);
  const applyLive = line => {
    clearTimeout(lineTimer.current);
    if (line) setTheirLine(line);
    else lineTimer.current = setTimeout(() => setTheirLine(null), 1200);
  };
  useEffect(() => { setTheirLine(null); }, [sketches]);
  useEffect(() => () => clearTimeout(lineTimer.current), []);

  const pairing = usePairing({
    role: companion ? 'companion' : hostCode && !readOnly ? 'host' : null,
    room: companion ? pairParam : hostCode,
    data,
    nav: { mode, step: stepKey, board: activeBoard },
    onNav: applyNav,
    onData: applyState,
    onInk: applyInk,
    onLive: applyLive,
  });
  const linked = companion ? pairing.connected : pairing.peers > 0;

  // Who may draw. A viewer may not; a companion may once the screen is there
  // to receive it. On a whiteboard the marker is always in hand.
  const inkLocked = companion ? !pairing.connected : readOnly;
  const penDown = mode === 'meet' && !inkLocked && (drawing || onBoard);

  const saveIllustration = useCallback(
    next => {
      if (inkLocked) return;
      putSketch(next);
    },
    [inkLocked, putSketch],
  );

  // The drawing that belongs to this slide, tied to what the slide shows. When
  // a figure on it changes, the slide starts a fresh drawing and the old one is
  // kept, with the page it was made on, in Illustrations and at the end of the
  // takeaway, so a mark never sits over a number it was not drawn about.
  const currentBasis = useMemo(() => slideBasis(stepKey, pages[stepKey], momentBoard), [stepKey, pages, momentBoard]);

  const earlier = findForMoment(sketches, stepKey, momentBoard);
  const saved = findForMoment(sketches, stepKey, momentBoard, currentBasis);
  const current = useMemo(() => {
    if (saved && sameBasis(saved.basis, currentBasis)) return saved;
    if (saved && !hasInk(saved)) return { ...saved, basis: currentBasis };
    return newIllustration({ kind: 'moment', step: stepKey, board: momentBoard, basis: currentBasis });
  }, [saved, currentBasis, stepKey, momentBoard]);

  const supersededHere = Boolean(!saved && earlier && hasInk(earlier));

  // What this device does to a drawing, said to the screen when this is the
  // iPad, which has already shown it here and leaves the saving to the screen.
  const tell = (op, ill) => {
    if (companion) pairing.sendInk({ op, id: ill.id, step: ill.step, board: ill.board || null, basis: ill.basis });
  };
  // A stroke finished here is added to the latest copy of the drawing, so one
  // that arrived from the other device while this was being drawn is kept.
  const { sendInk } = pairing;
  const commitInk = useCallback(next => {
    if (inkLocked) return;
    const stroke = next.strokes[next.strokes.length - 1];
    const latest = listIllustrations(sketchesRef.current).find(i => i.id === next.id);
    const merged = latest ? commitStroke({ ...latest, capture: latest.capture || next.capture }, stroke) : next;
    putSketch(merged);
    if (companion) sendInk({ op: 'stroke', id: merged.id, step: merged.step, board: merged.board || null, basis: merged.basis, stroke });
  }, [inkLocked, putSketch, companion, sendInk]);
  const undoInk = () => {
    if (inkLocked || !hasInk(current)) return;
    saveIllustration(undoStroke(current));
    tell('undo', current);
  };
  const clearInk = () => {
    if (inkLocked) return;
    saveIllustration(clearStrokes(current));
    tell('clear', current);
  };

  const live = useInk({
    surfaceRef: slideRef,
    listenRef: stageRef,
    drawing: penDown,
    color,
    illustration: current,
    onCommit: commitInk,
  });

  // The line being drawn here, shown there as it grows.
  const { sendLive } = pairing;
  useEffect(() => {
    if (!linked) return;
    sendLive(live ? { step: stepKey, board: momentBoard, stroke: live } : null);
  }, [live, linked, sendLive, stepKey, momentBoard]);

  const theirs = theirLine && theirLine.step === stepKey && (theirLine.board || null) === (momentBoard || null)
    ? theirLine.stroke
    : null;
  const shownStrokes = theirs ? [...strokesOf(current), theirs] : strokesOf(current);

  // The marker and Adjust never run together: Adjust adds controls to the
  // slide, and ink has to be laid over the slide exactly as it prints.
  const toggleDraw = useCallback(() => {
    setDrawing(v => {
      if (!v) setAdjusting(false);
      return !v;
    });
  }, []);
  const toggleAdjust = useCallback(() => {
    setAdjusting(v => {
      if (!v) setDrawing(false);
      return !v;
    });
  }, []);

  // D picks the marker up, Esc puts it down, E turns Adjust on and off. None
  // may steal a keystroke from a field, a select or an open dialog, and on a
  // whiteboard, where the marker is always in hand, none does anything. With
  // the brief beside the board only E does, for the goal sheet.
  useLayoutEffect(() => {
    if (mode !== 'meet' || (onBoard && SLIDE_FLOW)) return undefined;
    const onKey = e => {
      if (isEditableTarget(e.target)) return;
      if (document.querySelector('dialog[open]')) return;
      if (e.key === 'Escape' && drawing) {
        e.preventDefault();
        setDrawing(false);
        return;
      }
      const plain = !e.metaKey && !e.ctrlKey && !e.altKey;
      if ((e.key === 'd' || e.key === 'D') && plain && !inkLocked && !(BRIEF_FLOW && companion)) {
        e.preventDefault();
        toggleDraw();
      }
      if ((e.key === 'e' || e.key === 'E') && plain && !readOnly) {
        e.preventDefault();
        toggleAdjust();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, onBoard, drawing, inkLocked, readOnly, toggleDraw, toggleAdjust, companion]);

  // Drawing over the brief and the goal sheet (Jimmy, 2026-10-19). Beside the
  // whiteboard the marker is always in hand on the board; on the brief it is
  // picked up from the menu, or with D, and put down with Done or Esc, so the
  // brief can still be scrolled and its figures opened the rest of the time.
  const briefPen = BRIEF_FLOW && drawing && mode === 'meet' && !readOnly && !companion;
  const briefRef = useRef(null);
  const commitBriefInk = useCallback(stroke => {
    const latest = findForMoment(sketchesRef.current, ANCHORED_INK_STEP, null)
      || newIllustration({ kind: 'moment', step: ANCHORED_INK_STEP });
    putSketch({ ...latest, strokes: [...strokesOf(latest), stroke], updated: new Date().toISOString() });
  }, [putSketch]);
  const briefLive = useAnchoredInk({ rootRef: briefRef, enabled: briefPen, color, onCommit: commitBriefInk });
  const undoBriefInk = () => { if (briefInk && hasInk(briefInk)) putSketch(undoStroke(briefInk)); };
  const clearBriefInk = () => {
    if (!briefInk || !hasInk(briefInk)) return;
    if (window.confirm('Clear every mark drawn on the brief and the goal sheet? The whiteboards are not affected.')) putSketch(clearStrokes(briefInk));
  };
  const briefStrokes = useMemo(() => strokesOf(briefInk), [briefInk]);

  useEffect(() => {
    if (mode !== 'meet' && drawing) setDrawing(false);
  }, [mode, drawing]);

  // A new whiteboard, from the menu or the "+" beside the boards. There is
  // never more than one blank sheet: asking again while one is open stays on
  // it.
  const openWhiteboard = () => {
    const inked = boardKeys(sketches);
    setMode('meet');
    setStep(BOARD_INDEX);
    setBoardKey(inked.length ? nextBoardKey(inked) : FIRST_BOARD);
  };

  // The titled whiteboards of earlier versions still open in their own dialog.
  const closeLegacyBoard = () => {
    if (legacyBoard) saveIllustration(legacyBoard);
    setLegacyBoard(null);
  };

  const openSaved = ill => {
    setDrawerOpen(false);
    if (ill.kind === 'board') {
      setLegacyBoard(ill);
      return;
    }
    if (ill.step === BOARD_STEP) {
      setMode('meet');
      setStep(BOARD_INDEX);
      setBoardKey(ill.board || FIRST_BOARD);
      return;
    }
    setSavedPreview(ill);
  };

  // A slide that stops being available while it is open (statements
  // un-accepted, say) hands over to the nearest slide that is.
  useEffect(() => {
    if (!stepList[step].available) setStep(nextStep ?? prevStep ?? 0);
  }, [stepList, step, nextStep, prevStep]);
  useLayoutEffect(() => {
    setDrawing(false); setLegacyBoard(null); setSavedPreview(null); setDrawerOpen(false); setBoardKey(null);
  }, [demo.current]);
  useEffect(() => {
    const el=previewRef.current;if(!el)return;
    if(savedPreview&&!el.open)el.showModal();else if(!savedPreview&&el.open)el.close();
  }, [savedPreview]);

  const syncState = describeSync(sync);
  // The questionnaire's own save, which carries every figure corrected here.
  // It is separate from the assessment sync above, and only a problem with it
  // is shown outside the card: a correction that is not reaching Salesforce
  // should not look finished just because the page recalculated.
  const profileSave = useProfileSave();
  const bytes = illustrationsBytes(sketches);
  const tight = bytes > ASSESSMENT_LIMIT * 0.8;
  const toolStatus = syncState
    ? syncState.text
    : tight
      ? 'The drawings are close to the size Salesforce will hold'
      : '';

  // What the advisor controls about this slide, set beside it rather than on
  // it: what has been left out. The buttons that chose which years the income
  // reads from are gone; the cash slide marks both stretches on its picture
  // (Jimmy, 2026-10-01).
  const leftOut = (pages[stepKey]?.callouts || []).filter(c => c.hidden);
  const editingPage = adjusting && mode === 'meet' && (BRIEF_FLOW || !onBoard) && !readOnly;
  const showLeftOut = editingPage && leftOut.length > 0;

  const nextLabel = nextStep === undefined ? 'Review takeaway' : STEPS[nextStep].label;
  const goNext = () => (nextStep === undefined ? setMode('takeaway') : setStep(nextStep));
  const goBack = () => { if (prevStep !== undefined) setStep(prevStep); };

  // An iPad that has scanned the code and not yet heard from the screen has
  // nothing of its own to show.
  const waiting = companion && !pairing.joined;

  // What stops the packet being printed at all, as against what it merely says.
  const printBlocked = limitations.filter(l => l.blocking);

  // Beside the brief the marker is in hand on the board only, so the screen
  // is not "drawing": the goal sheet's figures still open.
  const screenClass = [
    'conv-screen',
    penDown && SLIDE_FLOW ? 'is-drawing' : '',
    BRIEF_FLOW ? 'is-split' : '',
    briefPen ? 'is-brief-drawing' : '',
    editingPage ? 'is-adjusting' : '',
    companion ? 'is-companion' : '',
    compact ? 'is-compact' : '',
  ].filter(Boolean).join(' ');

  // Whether the other device is there, in words.
  const linkText = companion
    ? (linked ? 'Connected to the meeting screen' : 'The meeting screen is not connected. Drawing is paused.')
    : hostCode
      ? (linked ? 'iPad connected' : 'Waiting for the iPad')
      : '';

  // The marker's card and the edit-mode badge float at the top left of the
  // white area. On a page with margins they sit in the margin; a whiteboard,
  // and every page on a phone or a tablet, has no margin, so room is kept for
  // them instead of letting them cover the sheet.
  const reserveTools = (penDown || editingPage) && (compact || onBoard);

  const boardChips = (
    <BoardChips
      boards={shownBoards}
      active={activeBoard}
      onPick={setBoardKey}
      onAdd={openWhiteboard}
      canAdd={!inkLocked && hasInk(current)}
    />
  );

  const meetingMenu = (
    <MeetingMenu
      readOnly={readOnly}
      canDraw={!inkLocked && !(BRIEF_FLOW && companion)}
      canAdjust={!readOnly && (BRIEF_FLOW || !onBoard)}
      boardOnly={BRIEF_FLOW}
      drawLocked={SLIDE_FLOW && onBoard}
      drawing={SLIDE_FLOW ? penDown : briefPen}
      onToggleDraw={toggleDraw}
      color={color}
      onColor={setColor}
      canUndo={!inkLocked && hasInk(current)}
      onUndo={undoInk}
      canClear={!inkLocked && hasInk(current)}
      onClear={() => setClearOpen(true)}
      clearOpen={clearOpen}
      onClearCancel={() => setClearOpen(false)}
      onClearConfirm={() => { clearInk(); setClearOpen(false); }}
      clearTitle={current.title}
      onWhiteboard={openWhiteboard}
      onDrawer={() => setDrawerOpen(true)}
      illustrationCount={drawings.length}
      status={toolStatus}
      adjusting={editingPage}
      onToggleAdjust={toggleAdjust}
      triangle={stepKey === 'tax' ? view.meeting.triangle : null}
      onToggleTriangle={() => setMeeting('triangle', !view.meeting.triangle)}
      onPair={!companion && !readOnly && pairing.available ? startPairing : null}
      paired={linked}
      onStatements={SLIDE_FLOW && compact && !companion ? () => setStatementsOpen(true) : null}
      onBrief={BRIEF_ENABLED && !companion ? openBrief : null}
      statementsLabel={STATEMENT_WORDS[analysis.availability?.state] || 'Statements'}
      link={compact && linkText ? { on: linked, text: linkText } : null}
      onBack={companion ? undefined : onBack}
    />
  );

  return (
    <MeetingProvider
      adjustable={mode === 'meet' && !readOnly}
      drawing={SLIDE_FLOW ? penDown : briefPen}
      adjusting={editingPage}
      hidden={hidden}
      onHide={hideCallout}
      onShow={showCallout}
      setMeeting={setMeeting}
      people={facts.people}
      assessmentSave={syncState}
      goalLead={view.goalLead}
    >
    <div className={screenClass} data-mode={mode} data-step={BRIEF_FLOW ? 'split' : stepKey}>
      <div className="conv-shell">
        {DEMO_MODE && demo.ready && (
          <DemoExampleBar
            households={demo.households}
            current={demo.current}
            onSelect={demo.select}
            onReset={demo.reset}
          />
        )}
        <header className="conv-head">
          <div className="conv-brand">
            <img className="conv-logo" src="/pure-logo-light.svg" alt="Pure Financial Advisors" />
            <span>Financial Assessment</span>
          </div>
          <div className="conv-head-right">
            {/* In the takeaway, printing is one small button beside the mode
                switch rather than a band across the top (Jimmy, 2026-10-05).
                When something stops the packet going out it is disabled, and
                the reason sits above the pages. */}
            {mode === 'takeaway' && !waiting ? (
              <button
                type="button"
                className="conv-btn is-small conv-print-btn"
                onClick={async () => {
                  if (!BRIEF_FLOW || !briefPrint.current) { window.print(); return; }
                  setBriefPrinting(true);
                  await briefPrint.current();
                  setBriefPrinting(false);
                }}
                disabled={BRIEF_FLOW ? !briefReady || briefPrinting : printBlocked.length > 0}
                title={BRIEF_FLOW
                  ? (briefReady ? undefined : 'Review the brief first')
                  : (printBlocked.length ? printBlocked.map(l => l.text).join(' ') : undefined)}
              >
                {briefPrinting ? 'Recording…' : 'Print / save PDF'}
              </button>
            ) : null}
            {/* Statements come in with the brief's documents in the brief flow. */}
            {SLIDE_FLOW ? (
              <button
                type="button"
                className="conv-btn is-small conv-statements-btn"
                data-state={analysis.availability?.state || 'absent'}
                onClick={() => setStatementsOpen(true)}
              >
                {STATEMENT_WORDS[analysis.availability?.state] || 'Statements'}
              </button>
            ) : null}
            {/* The menu, in the header (Jimmy, 2026-10-15); on a phone or a
                tablet the meeting's bar carries it instead. */}
            {BRIEF_FLOW && !(compact && mode === 'meet') ? meetingMenu : null}
            <div className="conv-modes" role="group" aria-label="Assessment mode">
              {MODES.map(m => (
                <button key={m.key} type="button" aria-pressed={mode === m.key} onClick={() => setMode(m.key)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        {waiting ? (
          <main className="conv-pair-wait">
            <img className="conv-logo" src="/pure-logo-light.svg" alt="Pure Financial Advisors" />
            {pairing.available !== false ? (
              <>
                <h2>Connecting to the meeting screen</h2>
                <p>Keep the assessment open on the screen you scanned the code from. This fills in as soon as it answers.</p>
              </>
            ) : (
              <>
                <h2>This site cannot link two devices yet</h2>
                <p>It needs a real-time service that has not been set up here.</p>
              </>
            )}
          </main>
        ) : mode === 'meet' && BRIEF_FLOW ? (
          /* The brief down the left and the whiteboard on the right (Jimmy,
             2026-10-15). On a phone or a tablet there is room for one of the
             two, and the bar's switch says which; a companion iPad draws. */
          <div
            ref={splitRef}
            className={`conv-meet-body is-split${compact ? ' is-compact' : ''}${!compact && !companion && split.hidden ? ` is-hidden-${split.hidden}` : ''}`}
            style={compact || companion ? undefined : { '--split': `${split.pct}%` }}
          >
            {compact ? (
              <SplitBar
                menu={meetingMenu}
                view={companion ? 'board' : splitView}
                onView={companion ? null : setSplitView}
                boards={boardChips}
                onTakeaway={companion ? null : () => setMode('takeaway')}
                link={linkText ? { on: linked, text: linkText } : null}
                pen={companion ? null : { on: briefPen, onToggle: toggleDraw, disabled: inkLocked }}
              />
            ) : null}
            {!companion && (!compact || splitView === 'brief') ? (
              <section className={`conv-split-brief${briefPen ? ' is-drawing' : ''}`} aria-label="The brief" ref={briefRef}>
                {editingPage ? <AdjustWidget onDone={toggleAdjust} /> : null}
                {briefPen ? (
                  <DrawWidget
                    color={color}
                    onColor={setColor}
                    canUndo={hasInk(briefInk)}
                    onUndo={undoBriefInk}
                    onClear={clearBriefInk}
                    canClear={hasInk(briefInk)}
                    onDone={toggleDraw}
                  />
                ) : null}
                {profileSave.problem && (
                  <div className="conv-save-alert no-print" role="alert">
                    <span>{profileSave.text}</span>
                    {profileSave.detail ? (
                      <details className="conv-save-detail">
                        <summary>What Salesforce said</summary>
                        <code>{profileSave.detail}</code>
                      </details>
                    ) : null}
                  </div>
                )}
                <Suspense fallback={<p className="conv-quiet">Loading the brief…</p>}>
                  <BriefBeside data={data} goals={pages.goals} onOpenBrief={openBrief} ink={{ strokes: briefStrokes, live: briefLive }} />
                </Suspense>
              </section>
            ) : null}
            {/* The line between them, which the advisor moves (Jimmy,
                2026-10-15). A hidden side keeps its place in the page, so the
                brief keeps where it was scrolled to and the board its scale. */}
            {!compact && !companion ? <SplitDivider split={split} containerRef={splitRef} /> : null}
            {companion || !compact || splitView === 'board' ? (
              <div className={`conv-split-board conv-meet-main${reserveTools && compact ? ' has-tools' : ''}`}>
                {!compact ? (
                  <div className="conv-board-strip no-print">
                    {penDown ? (
                      <DrawWidget color={color} onColor={setColor} canUndo={hasInk(current)} onUndo={undoInk} onClear={() => setClearOpen(true)} canClear={hasInk(current)} />
                    ) : null}
                    {linkText ? (
                      <p className={`conv-link-status${linked ? ' is-on' : ''}`} role="status" aria-live="polite">
                        <span aria-hidden="true" />
                        {linkText}
                      </p>
                    ) : null}
                    {boardChips}
                  </div>
                ) : penDown ? (
                  <DrawWidget color={color} onColor={setColor} canUndo={hasInk(current)} onUndo={undoInk} onClear={() => setClearOpen(true)} canClear={hasInk(current)} />
                ) : null}
                {compact && companion && !linked ? (
                  <div className="conv-link-alert no-print">
                    The meeting screen is not connected. Drawing is paused until it is back.
                  </div>
                ) : null}
                <div className="conv-stage-row">
                  <main className="conv-stage conv-meeting-canvas" ref={stageRef}>
                    <Slide ref={slideRef} strokes={shownStrokes} live={live} fit margin={0} minScale={0} maxScale={3}>
                      <PageBody step={stepKey} page={pages[stepKey]} asOf={asOf} />
                    </Slide>
                  </main>
                </div>
              </div>
            ) : null}
          </div>
        ) : mode === 'meet' ? (
          /* The steps and the meeting menu run down the left, so the page gets
             the full height of the window (Jimmy, 2026-10-02). On a phone, a
             tablet or a small window they are one slim bar instead. */
          <div className={`conv-meet-body${compact ? ' is-compact' : ''}`}>
            {compact ? (
              <CompactBar
                menu={meetingMenu}
                steps={stepList}
                groups={GROUPS}
                current={step}
                onPick={setStep}
                prevLabel={prevStep !== undefined ? STEPS[prevStep].label : ''}
                nextLabel={nextLabel}
                onBack={goBack}
                onNext={goNext}
                onTakeaway={() => setMode('takeaway')}
                pen={{
                  on: penDown,
                  disabled: inkLocked || onBoard,
                  title: onBoard ? 'The marker is always in hand on a whiteboard' : undefined,
                  onToggle: toggleDraw,
                }}
                boards={onBoard ? boardChips : null}
                link={linkText ? { on: linked, text: linkText } : null}
              />
            ) : (
              <aside className="conv-meeting-ribbon is-rail" aria-label="Meeting">
                <StepList
                  steps={stepList}
                  groups={GROUPS}
                  current={step}
                  onPick={setStep}
                  after={key => (key === 'meeting' && onBoard ? boardChips : null)}
                />
                {linkText ? (
                  <p className={`conv-link-status${linked ? ' is-on' : ''}`} role="status" aria-live="polite">
                    <span aria-hidden="true" />
                    {linkText}
                  </p>
                ) : null}
                {meetingMenu}
                {/* The takeaway, in plain sight at the foot of the column on
                    every page rather than inside the menu (Jimmy, 2026-10-05). */}
                <button type="button" className="conv-rail-takeaway" onClick={() => setMode('takeaway')}>
                  <TakeawayIcon />
                  Review takeaway
                </button>
              </aside>
            )}
            <div className={`conv-meet-main${reserveTools ? ' has-tools' : ''}`}>
              {/* The same floating card on every page, whiteboards included
                  (Jimmy, 2026-10-04). On a whiteboard the marker is always in
                  hand, so it offers Clear where a page offers Done. */}
              {penDown ? (
                <DrawWidget
                  color={color}
                  onColor={setColor}
                  canUndo={hasInk(current)}
                  onUndo={undoInk}
                  {...(onBoard
                    ? { onClear: () => setClearOpen(true), canClear: hasInk(current) }
                    : { onDone: toggleDraw })}
                />
              ) : null}
              {editingPage && !penDown ? <AdjustWidget onDone={toggleAdjust} /> : null}
              {compact && companion && !linked ? (
                <div className="conv-link-alert no-print">
                  The meeting screen is not connected. Drawing is paused until it is back.
                </div>
              ) : null}
              {profileSave.problem && (
                <div className="conv-save-alert no-print" role="alert">
                  <span>{profileSave.text}</span>
                  {profileSave.detail ? (
                    <details className="conv-save-detail">
                      <summary>What Salesforce said</summary>
                      <code>{profileSave.detail}</code>
                    </details>
                  ) : null}
                </div>
              )}
              <div className="conv-stage-row">
              <main className="conv-stage conv-meeting-canvas" ref={stageRef}>
                {(showLeftOut || supersededHere) && (
                  <div className="conv-advisor-strip no-print">
                    {showLeftOut && (
                      <div className="conv-left-out">
                        <span className="conv-quiet">Left out of this page, and listed here only in edit mode:</span>
                        {leftOut.map(c => (
                          <button type="button" className="conv-link" key={c.key} onClick={() => showCallout(c.key)}>
                            {`Bring back “${c.headline}”`}
                          </button>
                        ))}
                      </div>
                    )}
                    {supersededHere && (
                      <span className="conv-quiet">
                        The figures on this slide have changed since it was drawn on. The earlier drawing is kept, with the
                        page it was made on, in Illustrations.
                      </span>
                    )}
                  </div>
                )}
                {/* A whiteboard is given the whole white area: no room is kept
                    either side, and it may grow as large as the window lets
                    it. The steps turn the page there, not arrows beside it.
                    On a phone or a tablet every page is given the whole white
                    area, may be as small as the screen needs, and is turned
                    from the bar. */}
                <Slide
                  ref={slideRef}
                  strokes={shownStrokes}
                  live={live}
                  fit
                  margin={compact || onBoard ? 0 : undefined}
                  minScale={compact ? 0 : undefined}
                  maxScale={onBoard ? 3 : 1.4}
                  beside={compact || onBoard ? null : (
                    <>
                      {prevStep !== undefined ? (
                        <button type="button" className="conv-page-nav is-back no-print" onClick={goBack} aria-label={`Back: ${STEPS[prevStep].label}`}>
                          <span className="conv-page-nav-arrow" aria-hidden="true">←</span>
                          <span className="conv-page-nav-label" aria-hidden="true">{STEPS[prevStep].label}</span>
                        </button>
                      ) : null}
                      <button type="button" className="conv-page-nav is-next no-print" onClick={goNext} aria-label={`Next: ${nextLabel}`}>
                        <span className="conv-page-nav-arrow" aria-hidden="true">→</span>
                        <span className="conv-page-nav-label" aria-hidden="true">{nextLabel}</span>
                      </button>
                    </>
                  )}
                >
                  <PageBody step={stepKey} page={pages[stepKey]} asOf={asOf} />
                </Slide>
              </main>
              </div>
            </div>
          </div>
        ) : (
          <>
            {profileSave.problem && (
              <div className="conv-save-alert no-print" role="alert">
                <span>{profileSave.text}</span>
                {profileSave.detail ? (
                  <details className="conv-save-detail">
                    <summary>What Salesforce said</summary>
                    <code>{profileSave.detail}</code>
                  </details>
                ) : null}
              </div>
            )}
            <main className="conv-main">
              {BRIEF_FLOW ? (
                <Suspense fallback={<p className="conv-quiet">Loading the brief…</p>}>
                  <BriefTakeaway data={data} takeaway={takeaway} onOpenBrief={openBrief} onReady={setBriefReady} inkStrokes={briefStrokes} leadId={DEMO_MODE ? '' : leadId} printRef={briefPrint} />
                </Suspense>
              ) : (
                <TakeawayView takeaway={takeaway} blocked={printBlocked} />
              )}
            </main>
          </>
        )}

        {/* The meeting has no bottom bar: Back and Next sit in the page's
            margins and the save state is in the menu (Jimmy, 2026-10-02). */}
        {mode !== 'meet' && !waiting && (
          <footer className="conv-foot">
            <span className="conv-quiet">
              {facts.name ? `${facts.name} · ` : ''}
              {MODES.find(m => m.key === mode)?.label}
            </span>
            <div className="conv-actions">
              {companion ? (
                <button type="button" className="conv-btn is-small" onClick={() => setMode('meet')}>Back to the meeting</button>
              ) : null}
              {syncState ? <span className="conv-quiet">{syncState.text}</span> : null}
            </div>
          </footer>
        )}
      </div>

      <dialog ref={previewRef} className="conv-saved-dialog" aria-label="Saved illustration" onCancel={e=>{e.preventDefault();setSavedPreview(null);}}>
        <button type="button" className="conv-btn" onClick={()=>setSavedPreview(null)}>Close saved illustration</button>
        {savedPreview?.capture ? <SavedSlide capture={savedPreview.capture} strokes={savedPreview.strokes} fill="#fff"/> : savedPreview ? <p>This older drawing has no saved slide layout. Its original ink and figures are preserved in the takeaway; placement cannot be reconstructed reliably.</p> : null}
      </dialog>
      <StatementsDialog
        open={statementsOpen}
        onClose={() => setStatementsOpen(false)}
        demo={DEMO_MODE}
        readOnly={readOnly}
        name={facts.name}
        analysis={analysis}
        bundle={bundle}
        uploadStatus={uploadStatus}
        accepted={accepted}
        onAccept={() => setPrepare('analysisAccepted', currentBatch)}
        onUnaccept={() => setPrepare('analysisAccepted', '')}
        onBundle={next => {
          update('assessment.holdings', next);
          markChanged('analysis');
          // A new batch is a new review. The old acceptance is keyed to the
          // batch it was given for, so it cannot follow different holdings.
          setPrepare('analysisAccepted', '');
        }}
        onStatus={next => update('assessment.analysis', next)}
      />
      <WhiteboardDialog
        readOnly={readOnly}
        open={Boolean(legacyBoard)}
        board={legacyBoard}
        onChange={setLegacyBoard}
        onClose={closeLegacyBoard}
        color={color}
        onColor={setColor}
      />
      <PairDialog
        open={pairOpen}
        onClose={() => setPairOpen(false)}
        code={hostCode}
        leadId={DEMO_MODE ? '' : leadId}
        connected={pairing.peers > 0}
        available={Boolean(pairing.available)}
      />

      <IllustrationsDrawer
        readOnly={readOnly}
        open={drawerOpen}
        illustrations={drawings}
        onOpenItem={openSaved}
        onRemove={ill => {
          if (readOnly) return;
          update('assessment.sketches', removeIllustration(sketches, ill.id));
          markChanged('sketches');
        }}
        onClose={() => setDrawerOpen(false)}
        note={tight ? 'These drawings are close to the size Salesforce will hold for this record.' : ''}
      />
    </div>
    </MeetingProvider>
  );
}
